import { useRef, type ReactNode } from 'react';
import { Portal } from './Portal';
import { IconButton } from './IconButton';
import { useFocusTrap } from './useFocusTrap';
import './Dialog.css';

/**
 * DESIGN.md §9.1 `Dialog` (+ `FocusTrap` — MVP-11 #28 folded the shared focus-trap effect into
 * `useFocusTrap.ts`, used by both this and `Sheet.tsx`). DESIGN.md §6.1: "Dialogs trap focus,
 * `Esc` closes, focus returns to the invoking element." operate.md: "Modal as first thought...
 * exhaust inline alternatives first" — used sparingly in this subsystem (ShortcutSheet and the
 * session restore choice are genuinely modal; Settings is deliberately NOT a dialog).
 */
/**
 * MVP-357: `closeLabel` was optional with an English `?? 'Close'` fallback. The header close button
 * only exists when `title` does, so the two are paired rather than the label made unconditionally
 * required — a `labelledBy` dialog renders no header and has no button to name.
 */
export type DialogProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Pass an existing heading id instead of `title` when the dialog body already renders one. */
  labelledBy?: string;
  /** `md` (default, 560px — every existing caller) or `lg` (800px) for content that genuinely
   * needs more room, e.g. `LyricEditor`'s line list + per-line action row (round 2 fix: that
   * content is 720px wide by its own CSS, which the 560px default clipped — "Save lyrics" sat
   * 78px outside the panel with no way to reach it short of a horizontal scrollbar). */
  size?: 'md' | 'lg';
} & (
  | {
      /** Present -> renders the header, and with it the close button that must be named. */
      title: string;
      /** `ui/` stays dictionary-free (see `ui/SearchField.tsx`'s rule), so a caller passes
       * `t('common.close')`. Same shape as `Sheet.tsx`'s own `closeLabel`. */
      closeLabel: string;
    }
  | { title?: undefined; closeLabel?: undefined }
);

// `props` is read whole rather than destructured: `closeLabel`'s type is only known once `title` has
// been narrowed, and a destructured binding loses the link between the two.
export function Dialog(props: DialogProps) {
  const { open, onClose, children, labelledBy, size = 'md' } = props;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleId = useRef(`dialog-title-${Math.random().toString(36).slice(2)}`).current;

  useFocusTrap(panelRef, open, onClose);

  if (!open) return null;

  return (
    <Portal>
      <div className="dialog-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div
          className={`dialog-panel${size === 'lg' ? ' dialog-panel--lg' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy ?? (props.title !== undefined ? titleId : undefined)}
          ref={panelRef}
          tabIndex={-1}
        >
          {props.title !== undefined && (
            <div className="dialog-panel__header">
              {/* MVP-367: an `<h2>`, not a `<span>`. The dialog was already correctly NAMED
                  (`role="dialog"` + `aria-labelledby` pointing here), so nothing was unnamed — but a
                  screen-reader user navigating the open dialog by heading found nothing inside it,
                  and heading navigation is how most people move through a region they just entered.
                  `text-subtitle` still carries the size, so nothing changes visually; `Dialog.css`
                  zeroes the UA margin the tag brings with it. */}
              <h2 id={titleId} className="text-subtitle">
                {props.title}
              </h2>
              <IconButton icon="close" label={props.closeLabel} onClick={onClose} />
            </div>
          )}
          {children}
        </div>
      </div>
    </Portal>
  );
}
